import { Button } from '@oysterjs/ui/button';
import { messageFromError } from '@oysterjs/core/graphql/errors';

import { removeEmailBulkSendRecipients } from '~/api/data/email-bulk-send/mutations';
import type { BulkDraft } from '~/api/data/email-bulk-send/queries';
import {
  type BulkDraftRecipient,
  bulkDraftRecipientStatus,
  recipientLabel
} from './bulk-draft-recipient-status';

const REMOVE_FAILURE = "We couldn't remove this one. Try again.";

type RemoveOutcome = { bulkDraft: BulkDraft } | { failure: string };

/** Takes one client off the bulk draft, with its email. */
export const removeRecipient = async (
  bulkSendId: string,
  recipientId: string
): Promise<RemoveOutcome> => {
  try {
    const result = await removeEmailBulkSendRecipients({
      id: bulkSendId,
      recipientIds: [recipientId]
    });
    const bulkDraft = result.data?.removeEmailBulkSendRecipients;
    if (result.error || !bulkDraft) {
      return { failure: messageFromError(result.error, REMOVE_FAILURE) };
    }
    return { bulkDraft };
  } catch (removeError) {
    return { failure: messageFromError(removeError, REMOVE_FAILURE) };
  }
};

/** Remove, off while Cara drafts the email, with why the last try failed beside it. */
export const RemoveRecipientButton = ({
  recipient,
  error,
  onRemove
}: {
  recipient: BulkDraftRecipient;
  error: string | undefined;
  onRemove: () => void;
}) => {
  // The server refuses it too: the drafting run would leave its email behind.
  const isBeingDrafted = bulkDraftRecipientStatus(recipient) === 'drafting';
  return (
    <span className="inline-flex items-center gap-2">
      {error && (
        <span
          role="alert"
          className="max-w-48 whitespace-normal text-right text-xs text-red-600 dark:text-red-400"
        >
          {error}
        </span>
      )}
      <Button
        color="white"
        aria-label={`Remove ${recipientLabel(recipient)}`}
        title={isBeingDrafted ? 'Wait for Cara to finish drafting.' : undefined}
        disabled={isBeingDrafted}
        onClick={onRemove}
      >
        Remove
      </Button>
    </span>
  );
};
